import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import { Helmet } from 'react-helmet-async';

const SESSION_KEY = 'whitstable_phone_calls_unlocked';

const POWER_BI_DEFAULT_SRC =
  'https://app.powerbi.com/view?r=eyJrIjoiZWY5NTllOTMtN2VlZS00MzU1LWI5NmYtOTFmM2FjYjRiZTJkIiwidCI6ImUzNmQ3NWZhLWU4NjItNDAyMC05MzllLTU0YjFiNDQ2NGFlMSJ9';

function readUnlockedFromSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function PhoneCalls() {
  const configuredPassword = (process.env.REACT_APP_PHONE_CALLS_PASSWORD || '').trim();
  const dashboardSrc =
    (process.env.REACT_APP_PHONE_CALLS_DASHBOARD_URL || POWER_BI_DEFAULT_SRC).trim();

  const [unlocked, setUnlocked] = useState(readUnlockedFromSession);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!configuredPassword) {
        setError('This page is not configured. Please contact the home.');
        return;
      }
      if (password.trim() === configuredPassword) {
        try {
          sessionStorage.setItem(SESSION_KEY, '1');
        } catch {
          /* ignore quota / private mode */
        }
        setUnlocked(true);
        setPassword('');
      } else {
        setError('Incorrect password. Please try again.');
      }
    },
    [configuredPassword, password]
  );

  const handleLock = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
    setUnlocked(false);
    setPassword('');
    setError(null);
  }, []);

  return (
    <>
      <Helmet>
        <title>Phone Calls | Whitstable House</title>
        <meta
          name="description"
          content="Relatives phone call dashboard for Whitstable House. Password protected."
        />
        <link rel="canonical" href="https://www.whitstable.house/phone-calls/" />
      </Helmet>

      <div className="page">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="content-section phone-calls-page">
            <div className="phone-calls-container">
              <h1>Phone Calls</h1>

              {!unlocked ? (
                <div className="phone-calls-gate">
                  <p className="phone-calls-gate__intro">
                    Enter the password you were given to view the phone call dashboard.
                  </p>
                  {!configuredPassword && (
                    <p className="phone-calls-gate__warning" role="alert">
                      Access is not available until the site is configured with a password.
                    </p>
                  )}
                  <form className="phone-calls-form" onSubmit={handleSubmit} noValidate>
                    <label htmlFor="phone-calls-password" className="phone-calls-form__label">
                      Password
                    </label>
                    <input
                      id="phone-calls-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      className="phone-calls-form__input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? 'phone-calls-error' : undefined}
                    />
                    {error && (
                      <p id="phone-calls-error" className="phone-calls-form__error" role="alert">
                        {error}
                      </p>
                    )}
                    <button type="submit" className="phone-calls-form__submit">
                      View dashboard
                    </button>
                  </form>
                </div>
              ) : (
                <div className="phone-calls-dashboard">
                  <div className="phone-calls-dashboard__toolbar">
                    <button type="button" className="phone-calls-lock-btn" onClick={handleLock}>
                      Lock dashboard
                    </button>
                  </div>
                  <div className="power-bi-iframe-wrapper">
                    <iframe
                      title="Whitstable Phone Call Dashboard"
                      src={dashboardSrc}
                      className="power-bi-iframe"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>

        <footer className="footer">
          <section className="footer__enquiry">
            <div className="footer__enquiry-container">
              <Link to="/contact-us" className="footer__enquiry-link">
                Enquire about Whitstable House <span className="footer__arrow">▶</span>
              </Link>
            </div>
          </section>
          <div className="footer__container">
            <section className="contact-section">
              <div className="contact-section__content">
                <p>Whitstable House, Boorman Way, Whitstable CT5 3SE</p>
                <p>Copyright © Whitstable House</p>
              </div>
            </section>
          </div>
        </footer>
      </div>
    </>
  );
}

export default PhoneCalls;
